import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearSessionStorage } from "../../../public/api/httpClient";
import { getJwtRoleSlug } from "../../../public/utils/jwtRole";
import { getStoredAuthUser } from "../../../public/utils/authSession";
import { getAuthItem } from "../../../public/utils/authStorage";
import { isUnrestrictedAdminRole } from "../../../public/utils/roleAccess";
import NotificationsInbox from "../../notifications/components/NotificationsInbox";
import ProfileSettingsPanel from "../components/ProfileSettingsPanel";
import SyncStoragePanel from "../components/SyncStoragePanel";
import ThemeSettingsPanel from "../components/ThemeSettingsPanel";
import HelpSupportPanel from "../components/HelpSupportPanel";
import LegalPanel from "../components/LegalPanel";
import AboutPanel from "../components/AboutPanel";
import "../styles/SettingsPage.css";

const LOGOUT_BY_ROLE = {
  admin: "/admin-login",
  donor: "/donor-login",
  students: "/student-login",
};

function logoutPathForRole(roleSlug) {
  return LOGOUT_BY_ROLE[roleSlug] != null
    ? LOGOUT_BY_ROLE[roleSlug]
    : "/user-login";
}

function initialsFrom(name, email) {
  const raw = String(name || "").trim();
  if (raw) {
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
    }
    return raw.slice(0, 2).toUpperCase();
  }
  const e = String(email || "").trim();
  return e ? e.slice(0, 2).toUpperCase() : "?";
}

function formatRoleLabel(roleSlug) {
  return String(roleSlug || "user")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function IconUser() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
    </svg>
  );
}

function IconKey() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <circle cx="8" cy="15" r="4" />
      <path d="M10.7 12.3 19 4m0 0h-4m4 0v4" />
    </svg>
  );
}

function IconBell() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function IconPalette() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M12 21a9 9 0 1 1 0-18 9 9 0 0 1 8 12h-3a2.5 2.5 0 0 0 0 5h1" />
      <circle cx="7.5" cy="10.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
      <circle cx="16.5" cy="10.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconCloud() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M18 18H7a4 4 0 0 1-.5-8 6 6 0 0 1 11.5-1.5A3.5 3.5 0 0 1 18 18z" />
    </svg>
  );
}

function IconHelp() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 2.5-3 4.5" />
      <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconScale() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M12 3v18M5 7h14M7 7l-3 8h6l-3-8zm10 0-3 8h6l-3-8z" />
    </svg>
  );
}

function IconInfo() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 8h.01" />
    </svg>
  );
}

function IconLogout() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

const MENU_ITEMS = [
  {
    id: "profile",
    group: "Account",
    label: "Profile",
    Icon: IconUser,
    title: "Profile",
    status: "profile",
  },
  {
    id: "change-password",
    group: "Account",
    label: "Change Password",
    Icon: IconKey,
    title: "Change Password",
    status: "soon",
  },
  {
    id: "notifications",
    group: "Notifications",
    label: "Notifications",
    Icon: IconBell,
    title: "Notifications",
    status: "notifications",
  },
  {
    id: "theme",
    group: "Preferences",
    label: "Theme",
    Icon: IconPalette,
    title: "Theme",
    status: "theme",
  },
  {
    id: "sync-storage",
    group: "System",
    label: "Sync & Storage",
    Icon: IconCloud,
    title: "Sync & Storage",
    status: "storage",
  },
  {
    id: "help",
    group: "System",
    label: "Help & Support",
    Icon: IconHelp,
    title: "Help & Support",
    status: "help",
  },
  {
    id: "legal",
    group: "System",
    label: "Legal",
    Icon: IconScale,
    title: "Legal",
    status: "legal",
  },
  {
    id: "about",
    group: "System",
    label: "About",
    Icon: IconInfo,
    title: "About",
    status: "about",
  },
  {
    id: "logout",
    group: "System",
    label: "Logout",
    Icon: IconLogout,
    title: "Logout",
    status: "logout",
  },
];

function buildMenuGroups(items) {
  const groups = [];
  const seen = new Map();
  for (const item of items) {
    if (!seen.has(item.group)) {
      seen.set(item.group, []);
      groups.push({ name: item.group, items: seen.get(item.group) });
    }
    seen.get(item.group).push(item);
  }
  return groups;
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState("notifications");

  const profile = useMemo(() => {
    const stored = getStoredAuthUser() || null;
    const roleSlug =
      getJwtRoleSlug(getAuthItem("jwt_token")) || stored?.role || "";
    return {
      name: stored?.name || "",
      email: stored?.email || "",
      roleSlug,
      initials: initialsFrom(stored?.name, stored?.email),
    };
  }, []);

  const visibleMenuItems = useMemo(() => {
    const isAdmin = isUnrestrictedAdminRole(profile.roleSlug);
    return MENU_ITEMS.filter((item) => {
      if (item.id === "sync-storage") return isAdmin;
      if (item.id === "theme") return isAdmin;
      return true;
    });
  }, [profile.roleSlug]);

  const groups = useMemo(
    () => buildMenuGroups(visibleMenuItems),
    [visibleMenuItems],
  );
  const active = useMemo(
    () =>
      visibleMenuItems.find((item) => item.id === activeId) ||
      visibleMenuItems[0],
    [activeId, visibleMenuItems],
  );

  const handleLogout = () => {
    clearSessionStorage();
    navigate(logoutPathForRole(profile.roleSlug));
  };

  const ActiveIcon = active?.Icon;
  const isProfile = active?.status === "profile";
  const isNotifications = active?.status === "notifications";
  const isStorage = active?.status === "storage";
  const isTheme = active?.status === "theme";
  const isHelp = active?.status === "help";
  const isLegal = active?.status === "legal";
  const isAbout = active?.status === "about";
  const isInfoPanel = isHelp || isLegal || isAbout;

  return (
    <div className="settings-page">
      <header className="set-bar">
        <div className="set-bar__identity">
          <span className="set-bar__avatar" aria-hidden>
            {profile.initials}
          </span>
          <div className="set-bar__copy">
            <p className="set-bar__eyebrow">Settings</p>
            <h1 className="set-bar__title">Account & preferences</h1>
            <p className="set-bar__meta">
              {profile.name || "Signed in"}
              {profile.email ? ` · ${profile.email}` : ""}
            </p>
          </div>
        </div>
        <span className="set-chip">{formatRoleLabel(profile.roleSlug)}</span>
      </header>

      <div className={`set-split${isNotifications ? " set-split--fill" : ""}`}>
        <nav className="set-menu" aria-label="Settings sections">
          {groups.map((group) => (
            <div key={group.name} className="set-menu__group">
              <p className="set-menu__group-label">{group.name}</p>
              <ul className="set-menu__list">
                {group.items.map((item) => {
                  const Icon = item.Icon;
                  const selected = item.id === active.id;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={`set-menu__item${selected ? " is-active" : ""}${
                          item.status === "logout" ? " is-danger" : ""
                        }`}
                        onClick={() => setActiveId(item.id)}
                        aria-current={selected ? "page" : undefined}
                      >
                        <span className="set-menu__icon" aria-hidden>
                          <Icon />
                        </span>
                        <span className="set-menu__label">{item.label}</span>
                        {item.status === "soon" ? (
                          <span className="set-menu__soon">Soon</span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <main
          className={`set-detail${isNotifications ? " set-detail--inbox" : ""}${
            isStorage ? " set-detail--storage" : ""
          }${isTheme ? " set-detail--theme" : ""}${isProfile ? " set-detail--profile" : ""}${
            isInfoPanel ? " set-detail--info" : ""
          }`}
          aria-live="polite"
        >
          {isProfile ? (
            <ProfileSettingsPanel />
          ) : isNotifications ? (
            <NotificationsInbox embedded />
          ) : isStorage ? (
            <SyncStoragePanel />
          ) : isTheme ? (
            <ThemeSettingsPanel />
          ) : isHelp ? (
            <HelpSupportPanel />
          ) : isLegal ? (
            <LegalPanel />
          ) : isAbout ? (
            <AboutPanel />
          ) : (
            <div className="set-detail__center">
              <span className="set-detail__icon set-detail__icon--lg" aria-hidden>
                {ActiveIcon ? <ActiveIcon /> : null}
              </span>
              <p className="set-detail__group">{active?.group}</p>
              <h2 className="set-detail__title">{active?.title}</h2>

              {active?.status === "soon" ? (
                <p className="set-detail__soon-label">Coming soon</p>
              ) : null}

              {active?.status === "logout" ? (
                <button
                  type="button"
                  className="set-detail__cta set-detail__cta--danger"
                  onClick={handleLogout}
                >
                  <span>Logout now</span>
                  <IconLogout />
                </button>
              ) : null}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
